import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";

const SectionEditor = ({ s, i, total, onChange, onMove, onRemove }) => {
  const set = (k) => (e) => onChange({ ...s, [k]: e.target.value });
  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-6" data-testid={`tour-edit-section-${i}`}>
      <div className="flex items-center justify-between">
        <p className="font-display font-semibold">Section {i + 1}</p>
        <div className="flex gap-1">
          <Button size="icon" variant="ghost" type="button" disabled={i === 0} onClick={() => onMove(-1)} data-testid={`tour-move-up-${i}`}><ArrowUp className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" type="button" disabled={i === total - 1} onClick={() => onMove(1)} data-testid={`tour-move-down-${i}`}><ArrowDown className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" type="button" onClick={onRemove} data-testid={`tour-remove-${i}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5"><Label>Label</Label><Input value={s.eyebrow || ""} onChange={set("eyebrow")} data-testid={`tour-${i}-eyebrow`} /></div>
        <div className="space-y-1.5"><Label>Heading</Label><Input value={s.title || ""} onChange={set("title")} data-testid={`tour-${i}-title`} /></div>
      </div>
      <div className="space-y-1.5"><Label>Text</Label><Textarea rows={3} value={s.body || ""} onChange={set("body")} data-testid={`tour-${i}-body`} /></div>
      <div className="space-y-1.5"><Label>Bullet points (one per line)</Label>
        <Textarea rows={3} value={(s.bullets || []).join("\n")} onChange={(e) => onChange({ ...s, bullets: e.target.value.split("\n") })} data-testid={`tour-${i}-bullets`} />
      </div>
      <div className="grid gap-3 sm:grid-cols-[1fr_140px]">
        <div className="space-y-1.5"><Label>Screenshot URL</Label><Input value={s.image || ""} onChange={set("image")} placeholder="/images/tour/rota.jpg or https://…" data-testid={`tour-${i}-image`} /></div>
        {s.image && <img src={s.image} alt="" className="h-20 w-full rounded-md border border-slate-200 object-cover object-top" />}
      </div>
    </div>
  );
};

export const TourEditor = () => {
  const [c, setC] = useState(null);
  useEffect(() => {
    api.get("/super/cms", { params: { key: "tour" } }).then((r) => setC({ sections: [], ...r.data }));
  }, []);
  if (!c) return null;

  const setSection = (i, v) => setC({ ...c, sections: c.sections.map((x, j) => (j === i ? v : x)) });
  const move = (i, d) => {
    const s = [...c.sections];
    [s[i], s[i + d]] = [s[i + d], s[i]];
    setC({ ...c, sections: s });
  };
  const save = async () => {
    const content = { ...c, sections: c.sections.map((s) => ({ ...s, bullets: (s.bullets || []).map((b) => b.trim()).filter(Boolean) })) };
    try {
      await api.put("/super/cms", { content }, { params: { key: "tour" } });
      setC(content);
      toast.success("Product tour published");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="tour-editor">
      <div className="flex justify-end gap-3">
        <a href="/tour" target="_blank" rel="noreferrer" className="self-center text-sm font-semibold text-indigo-600" data-testid="tour-preview-link">View tour</a>
        <Button className="rounded-full" onClick={save} data-testid="tour-save-btn">Publish tour</Button>
      </div>
      <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-6">
        {[["title", "Page headline"], ["subtitle", "Intro text"], ["cta_title", "Closing headline"], ["cta_body", "Closing text"]].map(([k, l]) => (
          <div key={k} className="space-y-1.5"><Label>{l}</Label><Input value={c[k] || ""} onChange={(e) => setC({ ...c, [k]: e.target.value })} data-testid={`tour-${k}-input`} /></div>
        ))}
      </div>
      {c.sections.map((s, i) => (
        <SectionEditor key={i} s={s} i={i} total={c.sections.length} onChange={(v) => setSection(i, v)} onMove={(d) => move(i, d)}
          onRemove={() => setC({ ...c, sections: c.sections.filter((_, j) => j !== i) })} />
      ))}
      <Button variant="outline" className="w-full rounded-full" onClick={() => setC({ ...c, sections: [...c.sections, { eyebrow: "", title: "", body: "", bullets: [], image: "" }] })} data-testid="tour-add-section-btn">
        <Plus className="mr-1 h-4 w-4" />Add section
      </Button>
    </div>
  );
};
